/**
 * Standard Newznab category numbering, name-mapped for display.
 *
 * The Newznab/Torznab spec uses a stable 4-digit numbering scheme
 * for every standard category, and every public usenet indexer
 * follows it. Private indexers occasionally add their own codes
 * (5070, 5080 etc. used differently by some), but those are rare;
 * for anything not in this table the UI falls back to the numeric
 * id so the data isn't lost.
 *
 * Source: https://github.com/Prowlarr/Prowlarr/wiki/Indexer-Categories
 * and the canonical Newznab 0.x reference. Kept here as a hand
 * curated table rather than fetched from the indexer's `t=caps`
 * response because:
 *
 *   1. The standard numbering is identical across indexers — the
 *      names are too — so caching it per-indexer would duplicate
 *      data and require a schema change.
 *   2. Most users only ever see results tagged with these codes.
 *   3. When a private indexer does use a non-standard code, the
 *      numeric fallback is still readable: "5078" tells the user
 *      "TV: probably HD-anime or similar custom slot" rather than
 *      a useless name.
 *
 * To add per-indexer overrides later: extend the lookup with a
 * second argument `indexer_id` and load capability-shaped data from
 * an API call. The current shape — pure ID → name — keeps the call
 * sites simple.
 */

const STANDARD: Record<number, string> = {
  // 1000 — Console
  1000: "Console",
  1010: "Console/NDS",
  1020: "Console/PSP",
  1030: "Console/Wii",
  1040: "Console/Xbox",
  1050: "Console/Xbox 360",
  1060: "Console/Wiiware",
  1070: "Console/Xbox 360 DLC",
  1080: "Console/PS3",
  1090: "Console/Other",
  1110: "Console/3DS",
  1120: "Console/PS Vita",
  1130: "Console/WiiU",
  1140: "Console/Xbox One",
  1180: "Console/PS4",

  // 2000 — Movies
  2000: "Movies",
  2010: "Movies/Foreign",
  2020: "Movies/Other",
  2030: "Movies/SD",
  2040: "Movies/HD",
  2045: "Movies/UHD",
  2050: "Movies/BluRay",
  2060: "Movies/3D",
  2070: "Movies/DVD",
  2080: "Movies/WEB-DL",

  // 3000 — Audio
  3000: "Audio",
  3010: "Audio/MP3",
  3020: "Audio/Video",
  3030: "Audio/Audiobook",
  3040: "Audio/Lossless",
  3050: "Audio/Other",
  3060: "Audio/Foreign",

  // 4000 — PC
  4000: "PC",
  4010: "PC/0day",
  4020: "PC/ISO",
  4030: "PC/Mac",
  4040: "PC/Mobile-Other",
  4050: "PC/Games",
  4060: "PC/Mac-Other",
  4070: "PC/Mobile/iOS",
  4080: "PC/Mobile/Android",

  // 5000 — TV
  5000: "TV",
  5010: "TV/WEB-DL",
  5020: "TV/Foreign",
  5030: "TV/SD",
  5040: "TV/HD",
  5045: "TV/UHD",
  5050: "TV/Other",
  5060: "TV/Sport",
  5070: "TV/Anime",
  5080: "TV/Documentary",

  // 6000 — XXX
  6000: "XXX",
  6010: "XXX/DVD",
  6020: "XXX/WMV",
  6030: "XXX/XviD",
  6040: "XXX/x264",
  6050: "XXX/Pack",
  6060: "XXX/ImageSet",
  6070: "XXX/Other",

  // 7000 — Books
  7000: "Books",
  7010: "Books/Mags",
  7020: "Books/Ebook",
  7030: "Books/Comics",
  7040: "Books/Technical",
  7050: "Books/Other",
  7060: "Books/Foreign",

  // 8000 — Other
  8000: "Other",
  8010: "Other/Misc",
  8020: "Other/Hashed",
};

/**
 * Resolve a Newznab category id to its display name. Returns the
 * numeric id as a string when the id isn't in the standard table —
 * never returns `undefined`, so call sites don't need a fallback
 * branch.
 */
export function categoryName(id: number): string {
  return STANDARD[id] ?? String(id);
}

/**
 * Resolve a list of category ids to display names. De-dupes parent
 * labels: if a release is tagged with both `2000` (Movies) and `2040`
 * (Movies/HD), we drop the parent so the user just sees "Movies/HD".
 * Parent detection uses the spec's "ends in three zeros" rule.
 */
export function categoryNames(ids: number[]): string[] {
  if (ids.length === 0) return [];
  // Children take precedence over their parent.
  const hasChild = (parent: number) =>
    ids.some((c) => c !== parent && Math.floor(c / 1000) === Math.floor(parent / 1000) && c % 1000 !== 0);
  return ids
    .filter((id) => !(id % 1000 === 0 && hasChild(id)))
    .map(categoryName);
}
